import React from 'react';
import { Button, Box, Stack, Typography, Divider, TextField, Checkbox } from '@mui/material';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation } from 'react-fetching-library';
import { DebounceInput } from 'react-debounce-input';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { SmallFormTextField } from '../../../../../components/small-fields/SmallFormTextField';
import { AlertDialogContainer } from '../../../../../components/others/AlertDialogContainer';
import { submitDietAction } from '../../../../../services/request';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';


export default function PeriodDietForm (props) {
  const { data, submitCallback, handleClose, allSeasons, allPeriods } = props;

  const { loading, mutate } = useMutation(submitDietAction);
  const [showAlert, setShowAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
  const [periodDietPrice, setPeriodDietPrice] = React.useState(data ? data.diet_price_per_periods || [] : []);

  const periodDietPriceChange = (index, value) => {
    setPeriodDietPrice(prev => prev.map((item, i) => i === index ? { ...item, price: value } : item));
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('To pole nie może być puste'),
    periods: Yup.array().min(1, 'Przynajmniej jeden termin musi zostać wybrany')
      .required()
  });

  const initialValues = {
    name: data ? data.name || '' : '',
    season: data ? data.season || allSeasons[allSeasons.length - 1].value : allSeasons[allSeasons.length - 1].value,
    periods: data ? data.periods || [] : [],
    show_on_form: data ? data.show_on_form : true
  };

  const handleSubmit = async values => {
    const { error, payload } = await mutate(values, data ? 'PUT' : 'POST', data ? data.id : undefined);
    if (!error) {
      submitCallback(payload, data ? 'edit' : 'created', 'success', data ? 'Zaktualizowano diete' : 'Dodano nową diete', '');
      handleClose();
    } else {
      setSeverity('error');
      setTitle('Nie udało się dodać nowej diety');
      setMessage('');
      setShowAlert(true);
    }
  };

  const handleDelete = async () => {
    const { error, payload } = await mutate([], 'DELETE', data.id);
    if (!error) {
      submitCallback(data, 'delete', 'success', `Usunięto diete ${data.name}`, '');
      handleClose();
    } else {
      setSeverity('error');
      setTitle(`Nie udało się usunąć diety ${data.name}`);
      setMessage(payload);
      setShowAlert(true);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: values => {
      values.diet_price_per_periods = periodDietPrice;
      handleSubmit(values);
    }
  });

  return (
    <>
      {showAlert && (
        <AlertDialogContainer
          severity={severity}
          title={title}
          message={message}
          setShowAlert={setShowAlert}
        />
      )}
      <form onSubmit={formik.handleSubmit} style={{ textAlign: 'center' }}>
        <Box display="flex" flexDirection="column" mt={1} p={1}>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Nazwa</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="name"
              id="name"
              type="text"
              placeholder="nazwa diety"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              debounceTimeout={100}
              element={SmallFormTextField}
              autoFocus
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Sezon</Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              select
              id="season"
              name="season"
              options={allSeasons}
              value={formik.values.season}
              onChange={e => {
                formik.handleChange(e);
                formik.setFieldValue('periods', []);
                setPeriodDietPrice([]);
              }}
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Pokazuj na formularzu</Typography>
            <Box sx={{ flex: '1 1 90%', width: '60%', maxWidth: '1200px' }} display="flex" justifyContent="flex-start">
              <Checkbox
                id="show_on_form"
                name="show_on_form"
                checked={formik.values.show_on_form}
                onChange={formik.handleChange}
              />
            </Box>
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Terminy</Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              select
              multiple
              id="periods"
              name="periods"
              options={allPeriods[formik.values.season]}
              value={formik.values.periods}
              onChange={e => {
                formik.handleChange(e);
                setPeriodDietPrice(allPeriods[formik.values.season].filter(camp => e.target.value.includes(camp.value)).map(camp => ({ id: camp.value, name: camp.label, price: 0 })));
              }}
              error={formik.touched.periods && Boolean(formik.errors.periods)}
              helperText={formik.touched.periods && formik.errors.periods}
            />
          </Stack>
          <Divider sx={{ mb: 2 }}>Cena diety dla wybranych terminów za 1 dzień</Divider>
          <Stack justifyContent="center" alignItems="center">
            {periodDietPrice.map((item, index) => (
              <Stack key={item.id} direction="row" alignItems="center" sx={{ width: '60%' }} mb={2}>
                <Typography variant="subtitle2" flex="1 1 30%">{item.name}</Typography>
                <TextField
                  sx={{ flex: '1 1 65%' }}
                  type="number" size="small"
                  value={periodDietPrice[index].price}
                  onChange={e => periodDietPriceChange(index, e.target.value)}
                />
                <Typography variant="subtitle2" flex="1 1 5%">zł</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          sx={{ mt: 3 }}
          disabled={loading}
        >
          <AddIcon />
          Zapisz
        </Button>
        { data
          ? <Button
            color="error"
            variant="contained"
            sx={{ mt: 3, ml: 4 }}
            onClick={() => {
              setConfirmDeleteOpen(true);
            }}
            disabled={loading}
          >
            <DeleteIcon />
          Usuń
          </Button>
          : null}
      </form>
      <ConfirmDialog
        title="Usunąć diete?"
        open={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        onConfirm={handleDelete}
      >
        Czy na pewno chcesz usunąć tą diete?
      </ConfirmDialog>
    </>

  );
};
